import React, { useState } from 'react';
import EyeAILogo from '../images/EyeAILogo.jpg';

import api from '../api/axios';
import { encryptPassword } from '../utils/passwordCrypto';

const resolveVerifyResult = (action) => {
  if (action === 'CHANGE_PASSWORD') {
    return {
      targetPath: '/change-password',
      mustChangePassword: true,
    };
  }

  return {
    targetPath: null,
    mustChangePassword: false,
  };
};

const LoginPage = ({ onLogin }) => {
  // 'credentials' for username/password, '2fa' for the token
  const [loginStep, setLoginStep] = useState('credentials'); 
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const encryptedPassword = await encryptPassword(password);
      const response = await api.post(
        "login",
        `email=${encodeURIComponent(username)}&password=${encodeURIComponent(encryptedPassword)}`
      );
      
      // A 200 normally means "password accepted, now do 2FA". Accounts the server
      // exempts from 2FA (device logins under a partner in NO_2FA_CDP) come back
      // already logged in, with twoFactorRequired false and the usual verify action.
      if (response.status === 200) {
        if (response.data?.twoFactorRequired === false) {
          onLogin(resolveVerifyResult(response.data?.action));
          return;
        }
        setLoginStep('2fa');
      }
    } catch (error) {
      setError('Invalid username or password, or the account may be locked.');
      console.error('Credential verification failed.');
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token || !/^\d{6}$/.test(token)) {
      setError('Please enter a valid 6-digit authentication code.');
      return;
    }

    try {
      const response = await api.post(
        "verify",
        `onepin=${encodeURIComponent(token)}`
      );

      if (response.status === 200) {
        onLogin(resolveVerifyResult(response.data?.action));
        return;
      }

      setError('Invalid authentication code. Please try again.');
    } catch (err) {
      setError('An error occurred during verification. Please try again.');
      console.error('2FA verification failed.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        {/* Logo and Welcome Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={EyeAILogo} alt="EyeAI Logo" />
          </div>
          <h1 className="text-4xl font-bold text-indigo-900 mb-2">Welcome</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {loginStep === 'credentials' ? (
            <div>
              <h2 className="text-center text-2xl font-bold text-gray-800 mb-6">Sign in to your account</h2>
              <form className="space-y-6" onSubmit={handleCredentialSubmit}>
                            {/* Username Field */}
            <div className="mb-6">
              <label htmlFor="username" className="block text-gray-700 text-sm font-medium mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder=""
              />
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-gray-700 text-sm font-medium">
                  Password
                </label>
               
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder=""
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            {/* <div className="mb-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-gray-700 text-sm">Remember Me</span>
              </label>
            </div> */}

            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

            {/* Sign In Button */}
            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-md transition duration-200 ease-in-out transform hover:scale-[1.02]"
            >
              Sign In
            </button>
              </form>
            </div>
          ) : (
            <div>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800">Two-Factor Authentication</h2>
                <p className="mt-2 text-sm text-gray-600">Please enter the code from your authenticator app.</p>
              </div>
              <form className="mt-8 space-y-6" onSubmit={handle2FASubmit}>
                <div className="rounded-md shadow-sm">
                  <div>
                    <label htmlFor="token" className="sr-only">Authentication Code</label>
                    <input id="token" name="token" type="text" required autoFocus autoComplete="off" className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" placeholder="Authentication Code" value={token} onChange={(e) => setToken(e.target.value)} maxLength="6" />
                  </div>
                </div>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <div>
                  <button type="submit" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Verify
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
